/**
 * Copyright (c) 2020
 *
 * @summary calls to configuration/project API
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2020-11-17 10:21:56
 * Last modified  : 2020-11-17 15:31:17
 */

import axios from '@/api/axios.js'

export default {

    //*****************************\\
    //*********** CREATE ***********\\

    /**
     * @param subscriptionMetaId integer
     * @param projectConfiguration
     * @param async boolean
     * @returns Promise
     */
    createProject (subscriptionMetaId,projectConfiguration, async=false) {
        return axios.post(`/configuration/subscription-metas/${subscriptionMetaId}/projects?async=${async}`, projectConfiguration)
    },

    /**
     * @param projectId integer
     * @param keywords
     * @returns Promise
     */
    createKeyword (projectId, keywords) {
        return axios.post(`/configuration/projects/${projectId}/keywords`, keywords)
    },

    /**
     * @param projectId integer
     * @param keywordGroup
     * @returns Promise
     */
    createKeywordGroup (projectId, keywordGroup) {
        return axios.post(`/configuration/projects/${projectId}/keyword-groups`, keywordGroup)
    },

    /**
     * @param projectId integer
     * @param competitors
     * @returns Promise
     */
    createCompetitor (projectId, competitors) {
        return axios.post(`/configuration/projects/${projectId}/rivals`, competitors)
    },

    /**
     * @param projectId integer
     * @param searchGeolocationId integer
     * @returns Promise
     */
    addLocationToProject (projectId, searchGeolocationId) {
        return axios.post(`/configuration/projects/${projectId}/search-geolocations/${searchGeolocationId}`)
    },






    //*****************************\\
    //*********** READ ***********\\

    /**
     * @param subscriptionMetaId integer
     * @param withThumbnail boolean
     * @returns Promise
     */
    getProjects (subscriptionMetaId, withThumbnail=true) {
        return axios.get(`/configuration/subscription-metas/${subscriptionMetaId}/projects`,{params:{"with-thumbnail":withThumbnail}})
    },

    /**
     * @param subscriptionMetaId integer
     * @returns Promise
     */
    getProjectsSummary (subscriptionMetaId) {
        return axios.get(`/configuration/subscription-metas/${subscriptionMetaId}/projects/summaries`)
    },

    /**
     * @param projectId integer
     * @param withIncludeReport boolean
     * @returns Promise
     */
    getProjectInfo (projectId, withIncludeReport=true) {
        return axios.get(`/configuration/projects/${projectId}`,{params:{"include-reports":withIncludeReport}})
    },

    /**
     * @param projectId integer
     * @returns Promise
     */
    getKeywords (projectId) {
        return axios.get(`/configuration/projects/${projectId}/keywords`)
    },

    /**
     * @param projectId integer
     * @returns Promise
     */
    getKeywordGroups (projectId) {
        return axios.get(`/configuration/projects/${projectId}/keyword-groups`)
    },

    /**
     * @param projectId integer
     * @returns Promise
     */
    getCompetitors (projectId) {
        return axios.get(`/configuration/projects/${projectId}/rivals`)
    },
    /**
     * @param projectId integer
     * @returns Promise
     */
    getLocations (projectId) {
        return axios.get(`/configuration/projects/${projectId}/search-geolocations`)
    },



    /**
     * @returns Promise
     */
    getSearchEngines () {
        return axios.get(`/configuration/search-engines`)
    },



    /**
     * @param searchEngineId integer
     * @returns Promise
     */
    getSearchEngineLanguage (searchEngineId) {
        return axios.get(`configuration/search-engines/${searchEngineId}/language`)
    },

    /**
     * @param searchEngineId integer
     * @returns Promise
     */
    getSearchEngineGeolocs (searchEngineId) {
        return axios.get(`configuration/search-engines/${searchEngineId}/search-geolocations`)
    },




    /**
     * @param subscriptionId integer
     * @param subscriptionMeta
     * @returns Promise
     */
    createSubscriptionMeta (subscriptionId, subscriptionMeta) {
        return axios.post(`/configuration/subscriptions/${subscriptionId}/subscription-metas`, subscriptionMeta)
    },

    /**
     * @param subscriptionId integer
     * @returns Promise
     */
    getSubscriptionMeta (subscriptionId, withLogo=false) {
        return axios.get(`/configuration/subscription-metas/${subscriptionId}`,{params:{"with-logo":withLogo}})
    },

    /**
     * @param domainName string
     * @param locationCode integer
     * @param languageCode string
     * @returns Promise
     */
    getSuggestedKeywordByDomainName (domainName, locationCode, languageCode) {
        return axios.get(`suggest/keywords/by-domain/${domainName}/${locationCode}/${languageCode}`)
    },

    /**
     * @param domainName string
     * @param locationCode integer
     * @param languageCode string
     * @returns Promise
     */
    getSuggestedCompetitorByDomainName (domainName, locationCode, languageCode) {
        return axios.get(`suggest/competitors/by-domain/${domainName}/${locationCode}/${languageCode}`)
    },

    /**
     * @param creationJobId integer
     * @returns Promise
     */
    getCreateProjectStatus (creationJobId) {
        return axios.get(`/configuration/create-projects/${creationJobId}`)
    },

    /**
     * @param subscriptionId integer
     * @returns Promise
     */
    getCreateProjectBySubscription (subscriptionId) {
        return axios.get(`/configuration/subscription-metas/${subscriptionId}/create-projects`)
    },



    //*****************************\\
    //*********** UPDATE ***********\\

    /**
     * @param keywordGroupId integer
     * @param keywords
     * @returns Promise
     */
    addKeywordsToKeywordGroup (keywordGroupId, keywords) {
        return axios.post(`/configuration/keyword-groups/${keywordGroupId}/keywords`,keywords)
    },

    /**
     * @param projectId integer
     * @param project
     * @returns Promise
     */
    updateProject (projectId, project) {
        return axios.patch(`/configuration/projects/${projectId}`,project)
    },

    /**
     * @param subscriptionMetaId integer
     * @param subscriptionMeta
     * @returns Promise
     */
    updateSubscriptionMeta (subscriptionMetaId, subscriptionMeta) {
        return axios.patch(`/configuration/subscription-metas/${subscriptionMetaId}`,subscriptionMeta)
    },







    //*****************************\\
    //*********** DELETE ***********\\


    /**
     * @param projectId integer
     * @returns Promise
     */
    deleteProject (projectId) {
        return axios.delete(`/configuration/projects/${projectId}`)
    },


    /**
     * @param projectId integer
     * @param keyword
     * @returns Promise
     */
    deleteKeyword (projectId, keyword) {
        return axios.delete(`/configuration/projects/${projectId}/keywords`, {data:keyword})
    },

    /**
     * @param projectId integer
     * @param keywordGroups
     * @returns Promise
     */
    deleteKeywordGroups (projectId, keywordGroups) {
        return axios.delete(`/configuration/projects/${projectId}/keyword-groups`, {data:keywordGroups})
    },

    /**
     * @param keywordId integer
     * @param keywordGroups
     * @returns Promise
     */
    removeKeywordGroupsFromKeyword (keywordId, keywordGroups) {
        return axios.patch(`/configuration/keywords/${keywordId}/keyword-groups`,keywordGroups)
    },
    /**
     * @param keywordGroupId integer
     * @param keywords
     * @returns Promise
     */
    removeKeywordsFromKeywordGroup (keywordGroupId, keywords) {
        return axios.patch(`/configuration/keyword-groups/${keywordGroupId}/keywords`,keywords)
    },

    /**
     * @param projectId integer
     * @param competitors
     * @returns Promise
     */
    deleteCompetitors (projectId, competitors) {
        return axios.patch(`/configuration/projects/${projectId}/rivals`,competitors)
    },

    /**
     * @param projectId integer
     * @param searchGeolocationId integer
     * @returns Promise
     */
    deleteLocation (projectId, searchGeolocationId) {
        return axios.delete(`/configuration/projects/${projectId}/search-geolocations/${searchGeolocationId}`)
    },

};
